import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@repo/ui/components/ui/collapsible'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/ui/table'
import { CheckCircle2, ChevronDown, CreditCard, Download, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  AppWorkspaceContentPanel,
  AppWorkspacePanel,
  WorkspaceMetricStrip,
  WorkspaceSectionCard,
} from '@/components/app/workspace-shell'
import { trackEvent } from '@/lib/analytics'
import { APP_CONFIG } from '@/lib/config/constants'
import {
  normalizePlanId,
  PLAN_DEFINITIONS,
  type PlanDefinition,
  type SubscriptionPlan,
} from '@/lib/subscription'
import { trpc } from '@/lib/trpc'
import { formatLongDate } from '@/lib/utils/date'
import { handleMutationError } from '@/lib/utils/errors'

export function BillingTab() {
  const billingQuery = trpc.subscriptions.get.useQuery()
  const checkout = trpc.subscriptions.createCheckoutSession.useMutation()
  const paymentSetup = trpc.subscriptions.createPaymentSetupSession.useMutation()
  const portalSession = trpc.subscriptions.createPortalSession.useMutation()
  const changePlan = trpc.subscriptions.changePlan.useMutation()
  const [expanded, setExpanded] = useState(false)

  const isInitialLoading = billingQuery.isLoading && !billingQuery.data

  if (isInitialLoading) {
    return (
      <AppWorkspacePanel>
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" aria-label="Loading billing data" />
        </div>
      </AppWorkspacePanel>
    )
  }

  if (billingQuery.isError) {
    return (
      <AppWorkspacePanel>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 text-center">
          <div>
            <p className="text-lg font-semibold">Unable to load billing</p>
            <p className="text-sm text-muted-foreground">Stripe billing data is unavailable.</p>
          </div>
          <Button onClick={() => billingQuery.refetch()} variant="outline">
            Retry
          </Button>
        </div>
      </AppWorkspacePanel>
    )
  }

  if (!billingQuery.data) return null

  const subscription = billingQuery.data.subscription
  const history = billingQuery.data.history || []
  const currentPlanId: SubscriptionPlan = subscription ? normalizePlanId(subscription.plan) : 'free'
  const currentPriceId = subscription?.priceId ?? null
  const hasActiveSubscription = Boolean(subscription)
  const hasPaymentMethodOnFile = Boolean(billingQuery.data.defaultPaymentMethodId)
  const plans = PLAN_DEFINITIONS
  const currentPlanDefinition = plans.find((plan) => plan.id === currentPlanId) ?? plans[0]
  const statusLabel = subscription?.status
    ? subscription.status.replace(/_/g, ' ')
    : currentPlanDefinition.label
  const renewsOn = formatLongDate(subscription?.currentPeriodEnd ?? null)
  const trialEndsAt = formatLongDate(subscription?.trialEndsAt ?? null)
  const isBusy =
    checkout.isPending || paymentSetup.isPending || portalSession.isPending || changePlan.isPending
  const topHistory = history.slice(0, 3)

  const handleManageBilling = async () => {
    try {
      const result = await portalSession.mutateAsync({
        returnUrl: `${APP_CONFIG.BASE_URL}/billing`,
      })
      if (result.url) {
        window.location.href = result.url
      } else {
        toast.error('Unable to open billing portal.')
      }
    } catch (error) {
      handleMutationError(error, 'Failed to open billing portal. Please try again.')
    }
  }

  const handlePlanSelect = async (plan: PlanDefinition) => {
    if (!plan.priceId) {
      try {
        const result = await paymentSetup.mutateAsync({
          successUrl: `${APP_CONFIG.BASE_URL}/billing?success=true`,
          cancelUrl: `${APP_CONFIG.BASE_URL}/billing?canceled=true`,
        })
        if (result.url) {
          window.location.href = result.url
        }
      } catch (error) {
        handleMutationError(error, 'Failed to start payment setup. Please try again.')
      }
      return
    }

    if (hasActiveSubscription) {
      if (plan.priceId === currentPriceId) {
        toast.info(`${plan.label} is already active`)
        return
      }

      try {
        await changePlan.mutateAsync({ priceId: plan.priceId })
        toast.success(`Plan switched to ${plan.label}`)
        trackEvent('plan_changed', {
          from_plan: currentPlanId,
          to_plan: plan.id,
          change_type: 'upgrade',
        })
        billingQuery.refetch()
      } catch (error) {
        handleMutationError(error, 'Unable to update plan. Please try again later.')
      }
      return
    }

    try {
      const result = await checkout.mutateAsync({
        priceId: plan.priceId,
        successUrl: `${APP_CONFIG.BASE_URL}/billing?success=true`,
        cancelUrl: `${APP_CONFIG.BASE_URL}/billing?canceled=true`,
      })
      if (result.url) {
        window.location.href = result.url
      } else {
        toast.error('Unable to start checkout session.')
      }
    } catch (error) {
      handleMutationError(error, 'Failed to start checkout. Please try again.')
    }
  }

  return (
    <AppWorkspaceContentPanel
      eyebrow="Billing"
      description="Plan, payment method, and transaction history."
      contentClassName="space-y-6"
    >
      <section className="grid gap-4 xl:grid-cols-[0.9fr_1.3fr]">
        <WorkspaceSectionCard className="p-0">
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">Monthly</Badge>
              {hasPaymentMethodOnFile ? <Badge variant="secondary">Card on file</Badge> : null}
            </div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <CreditCard className="size-5" />
              Billing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <WorkspaceMetricStrip
              metrics={[
                {
                  icon: <CreditCard className="size-4" />,
                  label: 'Current plan',
                  value: currentPlanDefinition.label,
                  detail: `${statusLabel} · ${
                    trialEndsAt ? `Trial ends ${trialEndsAt}` : `Renews ${renewsOn || 'n/a'}`
                  }`,
                },
              ]}
            />
            <Button
              variant="outline"
              className="w-full cursor-pointer"
              onClick={handleManageBilling}
              disabled={portalSession.isPending}
            >
              {portalSession.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <CreditCard className="size-4" />
              )}
              Manage payment
            </Button>
          </CardContent>
        </WorkspaceSectionCard>

        <WorkspaceSectionCard className="p-0">
          <CardHeader>
            <CardTitle className="text-base">Plans</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            {plans.map((plan) => {
              const Icon = plan.icon
              const isCurrent = plan.id === currentPlanId

              return (
                <button
                  key={plan.id}
                  type="button"
                  className={`rounded-[6px] border p-4 text-left transition-colors ${
                    isCurrent
                      ? 'border-primary bg-primary/5'
                      : 'border-border/70 bg-background hover:border-primary/40'
                  }`}
                  onClick={() => handlePlanSelect(plan)}
                  disabled={isBusy || isCurrent}
                >
                  <div className="flex items-start justify-between gap-3">
                    <Icon className="size-5 text-primary" />
                    {isCurrent ? <CheckCircle2 className="size-4 text-primary" /> : null}
                  </div>
                  <p className="mt-3 font-semibold">{plan.label}</p>
                  <p className="text-sm text-muted-foreground">{plan.price}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{plan.description}</p>
                </button>
              )
            })}
          </CardContent>
        </WorkspaceSectionCard>
      </section>

      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger className="group flex w-full items-center justify-between rounded-[6px] border border-border/70 bg-card px-4 py-3 text-left">
          <div>
            <p className="text-base font-semibold">Transaction History</p>
            <p className="text-sm text-muted-foreground">
              {history.length === 0 ? 'No transactions yet' : `${history.length} transactions`}
            </p>
          </div>
          <ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>

        <CollapsibleContent className="pt-3">
          <div className="max-h-[300px] overflow-y-auto rounded-[6px] border border-border/70 bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Receipt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(expanded ? history : topHistory).map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatLongDate(item.date)}
                    </TableCell>
                    <TableCell className="text-sm">Subscription</TableCell>
                    <TableCell className="text-right font-medium">
                      {(item.amount / 100).toLocaleString('en-US', {
                        style: 'currency',
                        currency: item.currency,
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.invoiceUrl ? (
                        <a
                          href={item.invoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          Invoice <Download className="size-3.5" />
                        </a>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {history.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      Your billing history will appear here once you are on a paid plan.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </AppWorkspaceContentPanel>
  )
}
